import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { api } from "../lib/api";

const LINK_BY_KIND = { payment: "/wallet", booking: "/", reminder: "/", event: "/", friend: "/friends", message: "/messages", info: "/" };
const SEEN_KEY = "dc_seen_notif";

const getSeen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); } };
const addSeen = (id) => { const s = getSeen(); s.add(id); localStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-300))); };

export default function NotificationBanner() {
  const [banner, setBanner] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  const check = async () => {
    try {
      const { data } = await api.get("/notifications");
      const seen = getSeen();
      const next = (data || []).find((n) => !n.read && !seen.has(n.id));
      if (next) { addSeen(next.id); setBanner((b) => (b && b.id === next.id ? b : next)); }
    } catch { /* noop */ }
  };

  useEffect(() => {
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!banner) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setBanner(null), 8000);
    return () => clearTimeout(timer.current);
  }, [banner]);

  if (!banner) return null;

  const dismiss = (e) => { e?.stopPropagation(); addSeen(banner.id); setBanner(null); };
  const open = () => {
    addSeen(banner.id);
    api.post(`/notifications/${banner.id}/read`).catch(() => {});
    navigate(banner.link || LINK_BY_KIND[banner.kind] || "/");
    setBanner(null);
  };

  return (
    <div className="fixed top-[max(env(safe-area-inset-top),1rem)] left-4 z-[300] w-[min(20rem,calc(100vw-2rem))] animate-in slide-in-from-left-4 fade-in duration-300">
      <div onClick={open} data-testid="notification-banner" role="button"
        className="relative rounded-2xl bg-[#140F1E]/95 backdrop-blur-xl border border-[#D4AF37]/40 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.8)] p-4 pr-9 cursor-pointer active:scale-[0.99] transition-transform">
        <button onClick={dismiss} data-testid="notification-banner-close" aria-label="Dismiss" className="absolute top-2.5 right-2.5 text-[#A19DB0] hover:text-white"><X size={16} /></button>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center shrink-0"><Bell size={17} className="text-[#D4AF37]" /></div>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{banner.title}</p>
            {banner.body && <p className="text-[#A19DB0] text-xs mt-0.5 line-clamp-2">{banner.body}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
